import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Upload, RotateCcw, Zap, Target, BarChart3, Image as ImageIcon, X } from 'lucide-react';
import { loadModel, predictMalaria } from '../utils/api';

const PageContainer = styled.div`
  padding-top: 64px;
  min-height: calc(100vh - 64px);
  background: ${props => props.theme.colors.background};
`;

const DemoHeader = styled.div`
  padding: ${props => props.theme.spacing[10]} 0 ${props => props.theme.spacing[4]};
  max-width: 1200px;
  margin: 0 auto;
  padding-left: ${props => props.theme.spacing[6]};
  padding-right: ${props => props.theme.spacing[6]};
`;

const DemoTitle = styled.h1`
  font-size: ${props => props.theme.fontSizes['3xl']};
  margin-bottom: ${props => props.theme.spacing[2]};
  color: ${props => props.theme.colors.text.primary};
`;

const DemoSubtitle = styled.p`
  font-size: ${props => props.theme.fontSizes.base};
  color: ${props => props.theme.colors.text.secondary};
  margin-bottom: ${props => props.theme.spacing[2]};
  line-height: 1.6;
`;

const DemoContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing[6]} ${props => props.theme.spacing[12]};
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing[8]};

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing[6]};
  }
`;

const InputSection = styled.div`
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing[6]};
  box-shadow: ${props => props.theme.shadows.md};
`;

const SectionTitle = styled.h2`
  font-size: ${props => props.theme.fontSizes.xl};
  margin-bottom: ${props => props.theme.spacing[4]};
  color: ${props => props.theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
`;

const UploadArea = styled.div`
  border: 2px dashed ${props => props.isDragging ? props.theme.colors.primary : props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing[8]};
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.isDragging ? props.theme.colors.surface : 'transparent'};
  margin-bottom: ${props => props.theme.spacing[4]};

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background: ${props => props.theme.colors.surface};
  }
`;

const UploadInput = styled.input`
  display: none;
`;

const PreviewImage = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: ${props => props.theme.spacing[4]};
  border-radius: ${props => props.theme.borderRadius.md};
  overflow: hidden;
  border: 1px solid ${props => props.theme.colors.border};
`;

const Image = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

const RemoveButton = styled.button`
  position: absolute;
  top: ${props => props.theme.spacing[2]};
  right: ${props => props.theme.spacing[2]};
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  border-radius: ${props => props.theme.borderRadius.full};
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.9);
  }
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[6]};
  background: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.text.inverse};
  border: none;
  border-radius: ${props => props.theme.borderRadius.md};
  font-weight: ${props => props.theme.fontWeights.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: ${props => props.theme.fontSizes.base};

  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.accent};
    transform: translateY(-1px);
    box-shadow: ${props => props.theme.shadows.lg};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled(Button)`
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text.primary};
  border: 1px solid ${props => props.theme.colors.border};

  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.surface};
    border-color: ${props => props.theme.colors.primary};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing[3]};
  margin-top: ${props => props.theme.spacing[4]};
  flex-wrap: wrap;
`;

const ResultsSection = styled.div`
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing[6]};
  box-shadow: ${props => props.theme.shadows.md};
`;

const ResultCard = styled(motion.div)`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing[4]};
  margin-bottom: ${props => props.theme.spacing[4]};
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.md};
  }
`;

const ResultHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${props => props.theme.spacing[3]};
`;

const PredictionLabel = styled.span`
  padding: ${props => props.theme.spacing[1]} ${props => props.theme.spacing[3]};
  border-radius: ${props => props.theme.borderRadius.full};
  font-weight: ${props => props.theme.fontWeights.medium};
  font-size: ${props => props.theme.fontSizes.sm};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InfectedLabel = styled(PredictionLabel)`
  background: ${props => props.theme.colors.error};
  color: white;
`;

const UninfectedLabel = styled(PredictionLabel)`
  background: ${props => props.theme.colors.success};
  color: white;
`;

const ConfidenceBar = styled.div`
  width: 100%;
  height: 8px;
  background: ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.full};
  overflow: hidden;
  margin: ${props => props.theme.spacing[2]} 0;
`;

const ConfidenceFill = styled.div`
  height: 100%;
  background: ${props => props.confidence > 0.7 ? props.theme.colors.success : props.theme.colors.warning};
  border-radius: ${props => props.theme.borderRadius.full};
  transition: width 0.3s ease;
  width: ${props => props.confidence * 100}%;
`;

const ConfidenceText = styled.div`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.text.muted};
  text-align: right;
`;

const GradCAMImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: ${props => props.theme.borderRadius.md};
  margin-top: ${props => props.theme.spacing[4]};
  border: 1px solid ${props => props.theme.colors.border};
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${props => props.theme.spacing[4]};
  margin-top: ${props => props.theme.spacing[6]};
`;

const StatCard = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing[4]};
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.theme.colors.border};
`;

const StatNumber = styled.div`
  font-size: ${props => props.theme.fontSizes['2xl']};
  font-weight: ${props => props.theme.fontWeights.bold};
  color: ${props => props.theme.colors.accent};
  margin-bottom: ${props => props.theme.spacing[1]};
`;

const StatLabel = styled.div`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.text.secondary};
`;

const ErrorMessage = styled.div`
  background: ${props => props.theme.colors.error}20;
  border: 1px solid ${props => props.theme.colors.error};
  color: ${props => props.theme.colors.error};
  padding: ${props => props.theme.spacing[3]};
  border-radius: ${props => props.theme.borderRadius.md};
  margin-bottom: ${props => props.theme.spacing[4]};
  font-size: ${props => props.theme.fontSizes.sm};
`;
const DemoPage = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [results, setResults] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalAnalyzed: 0,
    averageConfidence: 0,
    infectedCount: 0,
    uninfectedCount: 0
  });
  const fileInputRef = useRef(null);

  const handleFileSelect = (file) => {
    if (file && file.type.startsWith('image/')) {
      setSelectedImage(file);
      setError(null);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setError('Please select a valid image file (PNG, JPG, JPEG)');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleAnalyze = async () => {
    if (!selectedImage) {
      setError('Please select an image first');
      return;
    }
    
    setIsAnalyzing(true);
    setError(null);
    
    try {
      let result;
      try {
        result = await predictMalaria(selectedImage, true);
      } catch (err) {
        const msg = (err && err.message) ? err.message : '';
        if (msg.includes('Model not loaded')) {
          await loadModel('models/malaria_detection_model.pth', 'efficientnet');
          result = await predictMalaria(selectedImage, true);
        } else {
          throw err;
        }
      }
      
      const predictionResult = {
        prediction: result.prediction,
        confidence: result.confidence,
        probabilities: result.probabilities,
        gradcamOverlay: result.gradcam_overlay,
        timestamp: new Date().toLocaleTimeString()
      };
      
      setResults(prev => [predictionResult, ...prev]);
      
      // Update stats
      setStats(prev => ({
        totalAnalyzed: prev.totalAnalyzed + 1,
        averageConfidence: ((prev.averageConfidence * prev.totalAnalyzed) + result.confidence) / (prev.totalAnalyzed + 1),
        infectedCount: result.prediction === 'Parasitized' ? prev.infectedCount + 1 : prev.infectedCount,
        uninfectedCount: result.prediction === 'Uninfected' ? prev.uninfectedCount + 1 : prev.uninfectedCount
      }));
      
      // Clear selection for next upload
      setSelectedImage(null);
      setImagePreview(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      setError(err.message || 'Failed to analyze image. Please check if the API server is running.');
      console.error('Analysis error:', err);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleReset = () => {
    setResults([]);
    setSelectedImage(null);
    setImagePreview(null);
    setError(null);
    setStats({
      totalAnalyzed: 0,
      averageConfidence: 0,
      infectedCount: 0,
      uninfectedCount: 0
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <PageContainer>
      <DemoHeader>
        <DemoTitle>Malaria Detection Demo</DemoTitle>
        <DemoSubtitle>
          Upload a blood smear image to detect malaria parasites. The system uses deep learning 
          models to classify images as infected or uninfected with confidence scores and visual explanations.
        </DemoSubtitle>
      </DemoHeader>

      <DemoContainer>
        <InputSection>
          <SectionTitle>
            <Target size={20} />
            Upload Image
          </SectionTitle>
          
          {error && <ErrorMessage>{error}</ErrorMessage>}
          
          {!imagePreview ? (
            <UploadArea
              isDragging={isDragging}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <ImageIcon size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
              <p style={{ marginBottom: '0.5rem', color: '#64748b' }}>
                Drag and drop an image here, or click to select
              </p>
              <p style={{ fontSize: '0.875rem', color: '#94a3b8' }}>
                Supported formats: PNG, JPG, JPEG
              </p>
              <UploadInput
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileInputChange}
              />
            </UploadArea>
          ) : (
            <PreviewImage>
              <Image src={imagePreview} alt="Preview" />
              <RemoveButton onClick={handleRemoveImage}>
                <X size={16} />
              </RemoveButton>
            </PreviewImage>
          )}
          
          <ButtonGroup>
            <Button onClick={handleAnalyze} disabled={isAnalyzing || !selectedImage}>
              {isAnalyzing ? (
                <>
                  <RotateCcw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                  Analyzing...
                </>
              ) : (
                <>
                  <Upload size={16} />
                  Analyze Image
                </>
              )}
            </Button>
            
            <SecondaryButton onClick={handleReset} disabled={isAnalyzing}>
              <RotateCcw size={16} />
              Reset
            </SecondaryButton>
          </ButtonGroup>
        </InputSection>

        <ResultsSection>
          <SectionTitle>
            <BarChart3 size={20} />
            Analysis Results
          </SectionTitle>
          
          {results.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
              <BarChart3 size={48} style={{ opacity: 0.3, marginBottom: '1rem' }} />
              <p>No analysis results yet. Upload an image and click "Analyze Image" to get started.</p>
            </div>
          ) : (
            <>
              {results.map((result, index) => {
                const isInfected = result.prediction === 'Parasitized';
                const LabelComponent = isInfected ? InfectedLabel : UninfectedLabel;
                
                return (
                  <ResultCard
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                  >
                    <ResultHeader>
                      <LabelComponent>{result.prediction}</LabelComponent>
                      <span style={{ fontSize: '0.875rem', color: '#64748b' }}>
                        {result.timestamp}
                      </span>
                    </ResultHeader>
                    
                    <div style={{ marginBottom: '0.5rem' }}>
                      <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.25rem' }}>
                        Confidence: {(result.confidence * 100).toFixed(1)}%
                      </div>
                      <ConfidenceBar>
                        <ConfidenceFill confidence={result.confidence} />
                      </ConfidenceBar>
                    </div>
                    
                    {result.probabilities && (
                      <div style={{ fontSize: '0.875rem', color: '#64748b', marginTop: '0.5rem' }}>
                        <div>Uninfected: {(result.probabilities.uninfected * 100).toFixed(1)}%</div>
                        <div>Parasitized: {(result.probabilities.parasitized * 100).toFixed(1)}%</div>
                      </div>
                    )}
                    
                    {result.gradcamOverlay && (
                      <div style={{ marginTop: '1rem' }}>
                        <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem' }}>
                          Grad-CAM Visualization:
                        </div>
                        <GradCAMImage 
                          src={`data:image/png;base64,${result.gradcamOverlay}`} 
                          alt="Grad-CAM Overlay" 
                        />
                      </div>
                    )}
                  </ResultCard>
                );
              })}

              <StatsGrid>
                <StatCard>
                  <StatNumber>{stats.totalAnalyzed}</StatNumber>
                  <StatLabel>Total Analyzed</StatLabel>
                </StatCard>
                <StatCard>
                  <StatNumber>{(stats.averageConfidence * 100).toFixed(1)}%</StatNumber>
                  <StatLabel>Avg Confidence</StatLabel>
                </StatCard>
                <StatCard>
                  <StatNumber>{stats.infectedCount}</StatNumber>
                  <StatLabel>Infected</StatLabel>
                </StatCard>
                <StatCard>
                  <StatNumber>{stats.uninfectedCount}</StatNumber>
                  <StatLabel>Uninfected</StatLabel>
                </StatCard>
              </StatsGrid>
            </>
          )}
        </ResultsSection>
      </DemoContainer>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </PageContainer>
  );
};

export default DemoPage;

